// Pure derived-summary helpers for the Home page and the top stats bar.
//
// Nothing in here touches Vue, the router, or the network. Inputs are the
// read-only match arrays owned by MatchesCache; outputs are small plain
// objects that components can assign in one step, so no large match graph
// ever becomes reactive component state.

const NONE = 'None'
const MIN_GAMES_FOR_MVP = 3 // Minimum games to be eligible for MVP

// Monday 00:00 in the browser's local timezone for the week containing `now`.
// Same boundary the top bar has always used, computed once per run instead
// of once per match.
export const getWeekStart = (now = new Date()) => {
  const today = new Date(now)
  const dayOfWeek = today.getDay() // 0 = Sunday, 1 = Monday, etc.
  const daysFromMonday = (dayOfWeek + 6) % 7 // Days to subtract to get to Monday
  const monday = new Date(today)
  monday.setDate(today.getDate() - daysFromMonday)
  monday.setHours(0, 0, 0, 0) // Set to start of day
  return monday
}

// Local midnight that starts the week after the one containing `now`. This is
// the only moment at which a weekly summary built from unchanged data can
// change, so callers schedule their next recompute for it.
export const getNextWeekStart = (now = new Date()) => {
  const next = getWeekStart(now)
  next.setDate(next.getDate() + 7)
  return next
}

// Normalized cache records carry `playedAtMs` (Date.parse of playedAtUtc, NaN
// when unparsable). Fall back to parsing for plain records, e.g. in tests.
const matchTime = (match) => {
  if (!match) return NaN
  if (typeof match.playedAtMs === 'number') return match.playedAtMs
  const playedAt = match.playedAtUtc
  if (!playedAt) return NaN
  return new Date(playedAt).getTime()
}

// Key holding the maximum, in plain-object key order. This is the original
// `Object.keys(counts).reduce((a, b) => counts[a] > counts[b] ? a : b)`, kept
// verbatim so tie behaviour is unchanged: on equal counts the later key wins,
// and integer-like keys iterate before string keys.
const maxKey = (counts) => {
  const keys = Object.keys(counts)
  if (keys.length === 0) return null
  return keys.reduce((a, b) => (counts[a] > counts[b] ? a : b))
}

export const emptyWeeklySummary = (now = new Date()) => ({
  weekStart: getWeekStart(now).getTime(),
  totalGames: 0,
  rankedGames: 0,
  normalGames: 0,
  mostPlayedHero: NONE,
  mostBannedHero: NONE,
  kills: 0,
  mvp: null,
})

/**
 * Consolidated weekly pass over the two match samples.
 *
 * @param {object} input
 * @param {Array} [input.ranked]  ranked matches (any version filter already applied)
 * @param {Array} [input.normal]  normal matches
 * @param {Date}  [input.now]
 * @returns {{
 *   weekStart: number, totalGames: number, rankedGames: number, normalGames: number,
 *   mostPlayedHero: string, mostBannedHero: string, kills: number,
 *   mvp: null | { playerId: number, name: string, winrate: string, games: number }
 * }}
 *
 * Semantics preserved from the previous per-render implementation:
 * - "this week" is `playedAtUtc >= local Monday 00:00`; unparsable dates are excluded
 * - hero counts, bans, kills and MVP come from ranked matches only
 * - hero key is `heroName || heroId`, ban entries are counted when truthy
 * - most played / most banned: on a tie the later key in object order wins
 * - MVP: bots (playerId <= 0) skipped, minimum 3 games, score strictly greater
 *   than the running best wins, initial best is 0, iteration in ascending
 *   numeric playerId order (plain object keys)
 */
export const computeWeeklySummary = ({ ranked = [], normal = [], now = new Date() } = {}) => {
  const monday = getWeekStart(now)
  const mondayMs = monday.getTime()

  let normalGames = 0
  for (let i = 0; i < normal.length; i++) {
    if (matchTime(normal[i]) >= mondayMs) normalGames++
  }

  let rankedGames = 0
  let kills = 0
  const heroCount = {}
  const banCount = {}
  const playerStats = {}

  for (let i = 0; i < ranked.length; i++) {
    const match = ranked[i]
    if (!(matchTime(match) >= mondayMs)) continue
    rankedGames++

    const bans = match.bans
    if (bans && Array.isArray(bans)) {
      for (let b = 0; b < bans.length; b++) {
        const bannedHero = bans[b]
        if (bannedHero) banCount[bannedHero] = (banCount[bannedHero] || 0) + 1
      }
    }

    const players = match.MatchData || []
    for (let p = 0; p < players.length; p++) {
      const player = players[p]

      const heroKey = player.heroName || player.heroId
      if (heroKey) heroCount[heroKey] = (heroCount[heroKey] || 0) + 1

      kills += player.kills || 0

      const playerId = player.playerId
      // Negative ids are bots - they have no account and cannot be MVP.
      if (playerId > 0) {
        let stats = playerStats[playerId]
        if (!stats) {
          stats = playerStats[playerId] = {
            totalFancyPoints: 0,
            totalKills: 0,
            totalAssists: 0,
            totalDeaths: 0,
            wins: 0,
            games: 0,
            playerId,
            name: player.name || '',
          }
        }
        stats.totalFancyPoints += player.fancyPoints || 0
        stats.totalKills += player.kills || 0
        stats.totalAssists += player.assists || 0
        stats.totalDeaths += player.deaths || 0
        stats.games += 1
        if (player.won) stats.wins += 1
      }
    }
  }

  if (rankedGames === 0) {
    const empty = emptyWeeklySummary(now)
    empty.normalGames = normalGames
    empty.totalGames = normalGames
    return empty
  }

  // Find MVP based on comprehensive scoring system
  let mvpPlayer = null
  let highestMvpScore = 0

  for (const playerId of Object.keys(playerStats)) {
    const stats = playerStats[playerId]

    // Skip players with insufficient games
    if (stats.games < MIN_GAMES_FOR_MVP) continue

    const avgFancyPoints = stats.totalFancyPoints / stats.games
    const winrate = stats.wins / stats.games
    const gamesPlayed = stats.games

    // MVP Score calculation:
    // - Base score from average fancy points (0-100+ range)
    // - Multiplied by winrate (0.0-1.0, heavily weighted)
    // - Games played bonus (diminishing returns after 10 games)
    // - Extra bonus for high winrate (>70%)
    const fancyPointsScore = avgFancyPoints
    const winrateMultiplier = Math.pow(winrate, 1.5) // Exponential winrate importance
    const gamesBonus = Math.min(gamesPlayed / 10, 1.2) // Max 20% bonus, diminishing returns
    const highWinrateBonus = winrate > 0.7 ? 1.1 : 1.0 // 10% bonus for >70% winrate

    const mvpScore = fancyPointsScore * winrateMultiplier * gamesBonus * highWinrateBonus

    if (mvpScore > highestMvpScore) {
      highestMvpScore = mvpScore
      mvpPlayer = {
        playerId: stats.playerId,
        name: stats.name,
        winrate: (winrate * 100).toFixed(1),
        games: stats.games,
      }
    }
  }

  return {
    weekStart: mondayMs,
    totalGames: rankedGames + normalGames,
    rankedGames,
    normalGames,
    mostPlayedHero: maxKey(heroCount) || NONE,
    mostBannedHero: maxKey(banCount) || NONE,
    kills,
    mvp: mvpPlayer,
  }
}

/**
 * Fallback for `MatchesCache.getDailyCounts` while that helper is unavailable.
 * Same semantics as the previous chart component: local one-month cutoff,
 * UTC `yyyy-mm-dd` labels, ascending by date. Returns `{ date, count }[]`.
 */
export const computeDailyCounts = (matches = [], now = new Date()) => {
  const countMap = {}
  const minDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate())
  const minMs = minDate.getTime()

  for (let i = 0; i < matches.length; i++) {
    const dateObj = new Date(matches[i].playedAtUtc)
    const ms = dateObj.getTime()
    // Skip when there is no valid date or it is too old (NaN fails both tests)
    if (!(ms >= minMs)) continue

    const date = dateObj.toISOString().split('T')[0] // yyyy-mm-dd
    countMap[date] = (countMap[date] || 0) + 1
  }

  return Object.entries(countMap)
    .sort(([a], [b]) => new Date(a) - new Date(b))
    .map(([date, count]) => ({ date, count }))
}
